import { RefObject, useEffect } from 'react';

/**
 * Focus `ref` on the next animation frame whenever `active` turns true.
 *
 * Deferring past one frame is the iframe-safe focus pattern. Focusing synchronously while an
 * accordion is mid-expand, a popover has just opened, or a form subtree has just mounted can be
 * swallowed by the ensuing layout pass — or, inside the embedded iframe, by the host re-anchoring
 * the iframe height — leaving the field unfocused (the reported "can't focus / can't type" symptom).
 * `preventScroll` stops the focus from scrolling the field into view, which would itself trigger a
 * height post that steals the focus straight back.
 *
 * Re-runs when `active` flips, so re-opening a form re-focuses its first field. This is the single
 * source for the focus-after-settle behaviour — do not re-roll a per-site `autoFocus` + rAF effect.
 */
export const useAutoFocusWhenVisible = (ref: RefObject<HTMLElement>, active = true): void => {
	useEffect(() => {
		if (!active) return undefined;
		const raf = requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }));
		return () => cancelAnimationFrame(raf);
	}, [active, ref]);
};
